import { useState, type ReactNode } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import {
  Badge,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuItem,
} from '@/components/ui';
import { ImportanceValue } from './FieldValue';
import type { FieldDefinition, FieldState, TypedFieldValue, FieldOption } from './typedFieldData';
import type { FieldCommit } from './fieldRegistry';
export interface ObjectStripItem {
  fieldId: string;
  icon: ReactNode;
  emptyLabel?: string;
  emptyAction?: string;
  /** A single filled status surface; nested appearance remains the default. */
  singleSurface?: boolean;
}
export function ObjectPropertyStrip({
  items,
  definitions,
  values,
  onCommit,
  readOnly = false,
}: {
  items: readonly ObjectStripItem[];
  definitions: readonly FieldDefinition[];
  values: Readonly<Record<string, FieldState>>;
  onCommit?: FieldCommit;
  readOnly?: boolean;
}) {
  const [pending, setPending] = useState<string | null>(null),
    [error, setError] = useState(false);
  return (
    <div data-slot="object-property-strip" className="flex min-w-0 flex-wrap items-center gap-2">
      {items.map((item) => {
        const def = definitions.find((d) => d.id === item.fieldId),
          state = values[item.fieldId];
        if (!def || !('options' in def)) return null;
        const current = state?.state === 'ready' ? state.value : null;
        const selected =
          current?.type === 'person' || current?.type === 'entity'
            ? current.value?.id
            : typeof current?.value === 'string'
              ? current.value
              : null;
        const option = def.options.find((o) => o.id === selected),
          empty = !selected;
        const text =
          state?.state === 'loading'
            ? 'Загрузка…'
            : state?.state === 'error'
              ? 'Недоступно'
              : (option?.label ?? (empty ? (item.emptyLabel ?? 'Не указано') : 'Неизвестное значение'));
        const disabled =
          readOnly || def.readOnly || !onCommit || state?.state !== 'ready' || pending !== null;
        const singleStatus = Boolean(item.singleSurface && def.type === 'status' && option);
        const content = singleStatus ? (
          <Badge
            variant={(option as FieldOption).tone ?? 'neutral'}
            solid
            className="min-h-8 gap-1.5 rounded-md px-2 text-xs"
          >
            <span aria-hidden className="[&_svg]:size-3.5">
              {item.icon}
            </span>
            <span>{text}</span>
            {!disabled && <ChevronDown aria-hidden className="size-3 shrink-0" />}
          </Badge>
        ) : (
          <>
            {(def.type !== 'importance' || !option) && (
              <span aria-hidden className="text-fg-subtle [&_svg]:size-3.5">
                {item.icon}
              </span>
            )}
            {def.type === 'status' && option ? (
              <Badge variant={(option as FieldOption).tone ?? 'neutral'} solid>
                {text}
              </Badge>
            ) : def.type === 'importance' && option ? (
              <ImportanceValue option={option as FieldOption} />
            ) : (
              <span className="min-w-0 break-words">{text}</span>
            )}
            {empty && !disabled && (
              <span className="font-medium text-brand-fg">{item.emptyAction ?? 'Выбрать'}</span>
            )}
            {!disabled && <ChevronDown aria-hidden className="size-3 shrink-0 text-fg-subtle" />}
          </>
        );
        return (
          <DropdownMenu key={item.fieldId}>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                data-object-property={item.fieldId}
                aria-label={
                  def.label + ': ' + text + (empty && !disabled ? ', ' + (item.emptyAction ?? 'Выбрать') : '')
                }
                disabled={disabled}
                className={
                  singleStatus
                    ? 'inline-flex min-h-8 max-w-full items-center rounded-md text-xs focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none disabled:cursor-default disabled:opacity-70'
                    : 'inline-flex min-h-8 max-w-full items-center gap-1.5 rounded-md border border-line px-2 py-1 text-xs text-fg-muted hover:bg-hover focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none disabled:cursor-default disabled:opacity-70'
                }
              >
                {content}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="max-h-72 max-w-72 overflow-y-auto">
              <DropdownMenuLabel>{def.label}</DropdownMenuLabel>
              {def.options.map((choice) => (
                <DropdownMenuItem
                  key={choice.id}
                  leftIcon={choice.id === selected ? <Check /> : undefined}
                  disabled={'disabled' in choice ? choice.disabled : undefined}
                  onSelect={() => {
                    if (!current || !onCommit || choice.id === selected) return;
                    const next = {
                      type: def.type,
                      value: def.type === 'person' || def.type === 'entity' ? choice : choice.id,
                    } as TypedFieldValue;
                    setPending(def.id);
                    setError(false);
                    void Promise.resolve()
                      .then(() => onCommit(def.id, next, current))
                      .catch(() => setError(true))
                      .finally(() => setPending(null));
                  }}
                >
                  {choice.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      })}
      {pending && (
        <span role="status" className="text-2xs text-fg-muted">
          Сохранение…
        </span>
      )}
      {error && (
        <p role="alert" className="text-xs text-danger-strong">
          Не удалось изменить свойство. Попробуй ещё раз.
        </p>
      )}
    </div>
  );
}
